import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/utils.ts'

const fieldBase =
  'w-full rounded-md border bg-white text-body text-gray-900 placeholder:text-gray-400 shadow-sm transition focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed'

const fieldTone = (error?: boolean) =>
  error
    ? 'border-red-400 focus:border-red-500 focus:ring-red-400/30'
    : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/30'

interface FieldWrapperProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  id: string
  children: ReactNode
  className?: string
}

function FieldWrapper({ label, hint, error, required, id, children, className }: FieldWrapperProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label ? (
        <label htmlFor={id} className="text-small font-medium text-gray-700">
          {label}
          {required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <span className="text-caption text-red-600">{error}</span>
      ) : hint ? (
        <span className="text-caption text-gray-500">{hint}</span>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  wrapperClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      leadingIcon,
      trailingIcon,
      className,
      wrapperClassName,
      id,
      required,
      ...props
    },
    ref,
  ) => {
    const autoId = useId()
    const fieldId = id ?? autoId
    const hasError = Boolean(error)
    const field = (
      <input
        ref={ref}
        id={fieldId}
        required={required}
        className={cn(
          fieldBase,
          fieldTone(hasError),
          'px-3 py-2',
          leadingIcon && 'pl-9',
          trailingIcon && 'pr-9',
          className,
        )}
        {...props}
      />
    )
    return (
      <FieldWrapper
        id={fieldId}
        label={label}
        hint={hint}
        error={error}
        required={required}
        className={wrapperClassName}
      >
        {leadingIcon || trailingIcon ? (
          <div className="relative">
            {leadingIcon ? (
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 [&>svg]:h-4 [&>svg]:w-4">
                {leadingIcon}
              </span>
            ) : null}
            {field}
            {trailingIcon ? (
              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 [&>svg]:h-4 [&>svg]:w-4">
                {trailingIcon}
              </span>
            ) : null}
          </div>
        ) : (
          field
        )}
      </FieldWrapper>
    )
  },
)
Input.displayName = 'Input'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  wrapperClassName?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, className, wrapperClassName, id, required, rows = 4, ...props }, ref) => {
    const autoId = useId()
    const fieldId = id ?? autoId
    const hasError = Boolean(error)
    return (
      <FieldWrapper
        id={fieldId}
        label={label}
        hint={hint}
        error={error}
        required={required}
        className={wrapperClassName}
      >
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          required={required}
          className={cn(fieldBase, fieldTone(hasError), 'px-3 py-2 resize-y', className)}
          {...props}
        />
      </FieldWrapper>
    )
  },
)
Textarea.displayName = 'Textarea'
